import { css } from "@emotion/css";
import type { SimpleMenuItem } from "@octopusdeploy/design-system-components";
import { accessibilityStyles, Button, resetStyles, SearchInput, SimpleMenu, useMenuState } from "@octopusdeploy/design-system-components";
import { EllipsisVerticalIcon, ListCollapseIcon, ListExpandIcon } from "@octopusdeploy/design-system-icons";
import { space, text, themeTokens } from "@octopusdeploy/design-system-tokens";
import { forwardRef, useCallback, useImperativeHandle, useState } from "react";
import type { ProcessSidebarStepData } from "./ProcessSidebarStep";
import { isProcessSidebarStepContainerData, ProcessSidebarStep } from "./ProcessSidebarStep";
export type ProcessSidebarRootStep = ProcessSidebarStepData & {
    stepNumber: number;
};
export interface ProcessSidebarProps {
    steps: ProcessSidebarRootStep[];
    searchValue: string;
    onSearchValueChange: (value: string) => void;
    menuItems: SimpleMenuItem[];
    showStepNumbers: boolean;
    onStepClick?: (step: ProcessSidebarStepData) => void;
}
export interface ProcessSidebarElement {
    expandStep: (stepId: string) => void;
}
export const ProcessSidebar = forwardRef<ProcessSidebarElement, ProcessSidebarProps>(({ steps, searchValue, onSearchValueChange, menuItems, showStepNumbers, onStepClick }, ref) => {
    const [collapsedContainerStepIds, setCollapsedContainerStepIds] = useState<Set<string>>(new Set());
    const expandStep = useCallback((stepId: string) => setCollapsedContainerStepIds((ids) => new Set([...ids].filter((id) => id !== stepId))), []);
    const collapseStep = useCallback((stepId: string) => setCollapsedContainerStepIds((ids) => new Set([...ids, stepId])), []);
    const containerSteps = steps.filter((step) => isProcessSidebarStepContainerData(step));
    const collapsibleContainerStepIds = new Set(containerSteps.filter((step) => !hasChildPreventingCollapse(step)).map((step) => step.id));
    const [onOverflowMenuButtonClick, overflowMenuState, overflowMenuButtonAriaAttributes] = useMenuState();
    // Any root container steps with children that have errors cannot be collapsed.
    // For any uncollapsible container steps, make sure the id is removed from state so it doesn't suddenly collapse later on if it becomes collapsible.
    const stepIdsToUncollapse = [...collapsedContainerStepIds].filter((stepId) => !collapsibleContainerStepIds.has(stepId));
    if (stepIdsToUncollapse.length > 0) {
        setCollapsedContainerStepIds((collapsedStepIds) => new Set([...collapsedStepIds].filter((stepId) => !stepIdsToUncollapse.includes(stepId))));
    }
    const showExpandCollapseAllButton = containerSteps.length > 0;
    const expandCollapseAllAction = collapsibleContainerStepIds.size === 0 || [...collapsibleContainerStepIds].some((stepId) => !collapsedContainerStepIds.has(stepId)) ? "collapse-all" : "expand-all";
    const expandCollapseAllButton = (<Button icon={expandCollapseAllAction === "collapse-all" ? <ListCollapseIcon size={20}/> : <ListExpandIcon size={20}/>} importance="quiet" disabled={collapsibleContainerStepIds.size === 0} onClick={() => setCollapsedContainerStepIds(expandCollapseAllAction === "collapse-all" ? collapsibleContainerStepIds : new Set())} accessibleName={expandCollapseAllAction === "collapse-all" ? "Collapse all steps" : "Expand all steps"}/>);
    useImperativeHandle(ref, () => ({
        expandStep,
    }), [expandStep]);
    return (<div className={styles.root}>
            <div className={styles.header}>
                <SearchInput placeholder={"Filter steps"} accessibleName={"Filter steps"} value={searchValue} onChange={onSearchValueChange} fullWidth={true}/>
                <div className={styles.headerActions}>
                    {showExpandCollapseAllButton ? expandCollapseAllButton : undefined}
                    <Button icon={<EllipsisVerticalIcon size={20}/>} importance="quiet" onClick={onOverflowMenuButtonClick} accessibleName={`${overflowMenuState.isOpen ? "Hide" : "Show"} process sidebar menu`} {...overflowMenuButtonAriaAttributes}/>
                    <SimpleMenu accessibleName={"Process sidebar menu"} items={menuItems} menuState={overflowMenuState}/>
                </div>
            </div>
            <ol className={styles.stepList} aria-label="Process sidebar steps">
                {steps.map((step) => {
            const isCollapsible = collapsibleContainerStepIds.has(step.id);
            const isCollapsed = collapsedContainerStepIds.has(step.id);
            const setIsCollapsed = (isCollapsed: boolean) => (isCollapsed ? collapseStep(step.id) : expandStep(step.id));
            return (<li key={step.id} className={styles.stepListItem}>
                            {showStepNumbers && (<div className={styles.stepNumber}>
                                    <span className={styles.stepAccessibilityPrefix}>Step&nbsp;</span>
                                    {step.stepNumber}
                                </div>)}
                            <ProcessSidebarStep stepData={step} isRootStep={true} onStepClick={onStepClick} isCollapsed={isCollapsible ? isCollapsed : undefined} setIsCollapsed={isCollapsible ? setIsCollapsed : undefined}/>
                        </li>);
        })}
            </ol>
        </div>);
});
function hasChildPreventingCollapse(step: ProcessSidebarStepData): boolean {
    return isProcessSidebarStepContainerData(step) && step.containerSteps.some((childStep) => childStep.errors.length > 0 || hasChildPreventingCollapse(childStep));
}
const styles = {
    root: css({
        // There is z-index used by RunInParallelIndicator inside ProcessSidebarStep component,
        // this isolation is needed to prevent z-index leaking.
        isolation: "isolate",
        display: "flex",
        flexDirection: "column",
        gap: space[8],
    }),
    header: css({
        display: "flex",
        gap: space[8],
    }),
    headerActions: css({
        display: "flex",
    }),
    stepList: css({
        ...resetStyles.list,
        display: "flex",
        flexDirection: "column",
        gap: space[8],
    }),
    stepListItem: css({
        position: "relative",
        display: "flex",
        gap: space[12],
    }),
    stepNumber: css({
        display: "flex",
        // This minWidth and marginLeft allows the number to bleed out into the left side padding if numbers get to triple or more digits
        minWidth: "1.625rem",
        marginLeft: `-${space[12]}`,
        justifyContent: "flex-end",
        font: text.body.regular.xSmall,
        color: themeTokens.color.text.secondary,
        padding: `${space[12]} 0`,
    }),
    stepAccessibilityPrefix: css({
        ...accessibilityStyles.hidden,
    }),
};
